import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Delete,
  EyeInvisible,
  EyeVisible,
  LayersFront,
  Upload,
} from '@factorialco/f0-react/icons/app'
import {
  ParticipantEmployeeFragment,
  TrainingListPageCompetencyFragment,
  TrainingsCategory,
  TrainingsDataCollectionDocument,
} from 'generated/resources/graphql'
import { TrainingsDataCollectionFragment } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'
import BulkDelete from 'modules/trainings/pages/Trainings/BulkDelete'
import UpdateCatalog from 'modules/trainings/pages/Trainings/UpdateCatalog'

export const TrainingsDataCollection = ({
  canCreate,
  competencies,
  categories,
  employeesData,
}: {
  canCreate: boolean
  competencies?: TrainingListPageCompetencyFragment[]
  categories?: TrainingsCategory[]
  employeesData: ParticipantEmployeeFragment[]
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [updateCatalog, setUpdateCatalog] = useState<{
    action: 'display' | 'hide'
    itemIds: number[]
  } | null>(null)
  const [bulkDeleteItems, setBulkDeleteItems] = useState<number[] | null>(null)
  const source = useGraphqlDataCollectionSource({
    document: TrainingsDataCollectionDocument,
    paginationType: 'pages',
    perPage: 30,
    mapPagination: (data) => {
      return {
        total: data.trainings.trainingsConnection.totalCount ?? 0,
      }
    },
    mapData: (data) => data.trainings.trainingsConnection.nodes,
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('trainings.trainings_table.name'),
      },
    },
    defaultSorting: {
      field: 'name',
      order: 'asc',
    },
    mapVariables: ({ filters, search, sortings }) => {
      const filterObject: Record<string, unknown> = {}

      if (filters.competencies && filters.competencies.length > 0) {
        filterObject.competencyId = { in: filters.competencies }
      }

      if (filters.categories && filters.categories.length > 0) {
        filterObject.categoryId = { in: filters.categories }
      }

      if (filters.employee && filters.employee.length > 0) {
        filterObject.accessId = { in: filters.employee }
      }

      if (filters.year && filters.year.length > 0) {
        filterObject.year = { in: filters.year }
      }

      if (filters.courseExpiration && filters.courseExpiration.length > 0) {
        filterObject.withExpiredMemberships = { eq: filters.courseExpiration?.[0] === 'expired' }
      }

      return {
        search: search,
        status: filters.status?.[0],
        filter: Object.keys(filterObject).length > 0 ? filterObject : undefined,
        sortOrder: sortings?.[0]
          ? { field: sortings[0].field, order: sortings[0].order }
          : undefined,
      }
    },
    presets: [
      {
        label: i18n.t('trainings.trainings_table.expired_participants_label'),
        filter: { courseExpiration: ['expired'] },
      },
      {
        label: i18n.t('trainings.trainings_table.status.active'),
        filter: { status: ['active'] },
      },
      {
        label: i18n.t('trainings.trainings_table.creation_year', {
          year: new Date().getFullYear(),
        }),
        filter: { year: [new Date().getFullYear()] },
      },
    ],
    filters: {
      status: {
        type: 'in',
        label: i18n.t('trainings.trainings_table.status.filter'),
        options: {
          options: [
            {
              label: i18n.t('trainings.trainings_table.status.active'),
              value: 'active',
            },
            {
              label: i18n.t('trainings.trainings_table.status.draft'),
              value: 'draft',
            },
          ],
        },
      },
      competencies: {
        type: 'in',
        label: i18n.t('trainings.trainings_table.competencies'),
        options: {
          options:
            competencies?.map((competency) => ({
              label: competency.name,
              value: Number(competency.id),
            })) ?? [],
        },
      },
      categories: {
        type: 'in',
        label: i18n.t('trainings.trainings_table.tags'),
        options: {
          options:
            categories?.map((category) => ({
              label: category.name,
              value: Number(category.id),
            })) ?? [],
        },
      },
      employee: {
        type: 'in',
        label: i18n.t('trainings.trainings_table.participant'),
        options: {
          options: employeesData.map((employee) => ({
            label: employee.fullName,
            value: Number(employee.accessId),
          })),
        },
      },
      year: {
        type: 'in',
        label: i18n.t('trainings.create_training_modal.year'),
        options: {
          options: (() => {
            const currentYear = new Date().getFullYear()
            const years = []
            for (let i = 0; i < 10; i++) {
              const year = currentYear - i
              years.push({
                label: String(year),
                value: year,
              })
            }
            return years
          })(),
        },
      },
      courseExpiration: {
        type: 'in',
        label: i18n.t('trainings.trainings_table.filters.expiration_filter.title'),
        options: {
          options: [
            {
              label: i18n.t('trainings.trainings_table.filters.expiration_filter.yes'),
              value: 'expired',
            },
            {
              label: i18n.t('trainings.trainings_table.filters.expiration_filter.no'),
              value: 'not_expired',
            },
          ],
        },
      },
    },
    itemUrl: (item) =>
      navigation.trainings.list.details_revamp.view.overview.fullPath({
        id: String(item.id),
      }),
    primaryActions: () =>
      canCreate
        ? {
            label: i18n.t('trainings.trainings_table.add'),
            icon: Add,
            onClick: () => {
              track(trackingEvents.training.trainingCreationWizard.goToNewCourseWizard)
              navigate(navigation.trainings.list.index.new.fullPath())
            },
          }
        : undefined,
    secondaryActions: {
      expanded: 0,
      actions: () => [
        {
          label: i18n.t('trainings.trainings_table.export_name'),
          description: i18n.t('trainings.trainings_table.export_description'),
          icon: Upload,
          onClick: () => {
            navigate(navigation.trainings.list.index.export.fullPath())
          },
        },
      ],
    },
    itemActions: (item) => [
      {
        label: i18n.t('trainings.trainings_table.duplicate'),
        icon: LayersFront,
        onClick: () => {
          track(trackingEvents.training.duplicate.goToDuplicateCourse)
          navigate(navigation.trainings.list.index.duplicate.fullPath({ id: Number(item.id) }))
        },
      },
      ...(item.status !== 'draft'
        ? [
            {
              label: item.catalog
                ? i18n.t('trainings.catalog_remove')
                : i18n.t('trainings.catalog_display'),
              icon: item.catalog ? EyeInvisible : EyeVisible,
              onClick: () => {
                setUpdateCatalog({
                  action: item.catalog ? 'hide' : 'display',
                  itemIds: [Number(item.id)],
                })
              },
            },
          ]
        : []),
      {
        label: i18n.t('trainings.trainings_table.delete'),
        icon: Delete,
        critical: true,
        onClick: () => {
          setBulkDeleteItems([Number(item.id)])
        },
      },
    ],
    selectable: (item) => item.id,
    bulkActions: (items) => {
      const hasAnyDraftItems = items.itemsStatus
        .filter((item) => item.checked)
        .some((item) => item.item.status === 'draft')

      return {
        primary: hasAnyDraftItems
          ? []
          : [
              {
                id: 'display-catalog',
                label: i18n.t('trainings.trainings_table.display_catalog'),
              },
              {
                id: 'hide-catalog',
                label: i18n.t('trainings.trainings_table.hide_catalog'),
              },
            ],
        secondary: [
          {
            id: 'delete',
            label: i18n.t('common.delete'),
          },
        ],
      }
    },
  })

  const handleBulkAction = (action: string, items: TrainingsDataCollectionFragment[]) => {
    const itemIds = items.map((item) => Number(item.id))

    switch (action) {
      case 'display-catalog':
        setUpdateCatalog({ action: 'display', itemIds })
        break
      case 'hide-catalog':
        setUpdateCatalog({ action: 'hide', itemIds })
        break
      case 'delete':
        setBulkDeleteItems(itemIds)
        break
    }
  }

  return (
    <Box gap="s8">
      <OneDataCollection
        id="trainings/trainings/v1"
        storage={DEFAULT_DATA_COLLECTION_STORAGE}
        source={source}
        onBulkAction={(action, items) =>
          handleBulkAction(
            action,
            items.itemsStatus.filter((item) => item.checked).map((item) => item.item)
          )
        }
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              allowColumnHiding: true,
              columns: [
                {
                  label: i18n.t('trainings.trainings_table.name'),
                  sorting: 'name',
                  render: (item) => ({
                    type: 'text',
                    value: item.name,
                  }),
                },
                {
                  label: i18n.t('trainings.trainings_table.participants'),
                  render: (item) => ({
                    type: 'number',
                    value: item.totalParticipants,
                  }),
                },
                {
                  label: i18n.t('trainings.trainings_table.expired_participants_label'),
                  render: (item) => {
                    const hasExpired =
                      item.numberOfExpiredParticipants && item.numberOfExpiredParticipants > 0

                    return {
                      type: 'status',
                      value: {
                        status: hasExpired ? 'warning' : 'positive',
                        label: i18n.t('trainings.trainings_table.expired_participants.field', {
                          count: item.numberOfExpiredParticipants || 0,
                        }),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('trainings.trainings_table.catalog'),
                  render: (item) => {
                    if (!item.catalog) {
                      return {
                        type: 'text',
                        value: '-',
                      }
                    }

                    return {
                      type: 'icon',
                      value: {
                        icon: EyeVisible,
                        label: i18n.t('trainings.on_catalog'),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('trainings.trainings_table.status.status_title'),
                  render: (item) => {
                    if (!item.status) {
                      return {
                        type: 'text',
                        value: '-',
                      }
                    }

                    const getStatusColor = (status: string): 'neutral' | 'positive' => {
                      return status === 'active' ? 'positive' : 'neutral'
                    }

                    const getStatusLabel = (status: string): string => {
                      return status === 'active'
                        ? i18n.t('trainings.trainings_table.status.active')
                        : i18n.t('trainings.trainings_table.status.draft')
                    }

                    return {
                      type: 'status',
                      value: {
                        status: getStatusColor(item.status),
                        label: getStatusLabel(item.status),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('trainings.trainings_table.tags'),
                  render: (item) => ({
                    type: 'tagList',
                    value: {
                      type: 'dot',
                      tags: item.categories.map((category) => ({
                        text: category.name,
                        customColor: '#fff',
                      })),
                      max: 3,
                    },
                  }),
                },
                {
                  label: i18n.t('trainings.trainings_table.competencies'),
                  render: (item) => ({
                    type: 'tagList',
                    value: {
                      type: 'dot',
                      tags: item.competencies.map((competency) => ({
                        text: competency.name,
                        customColor: '#fff',
                      })),
                      max: 3,
                    },
                  }),
                },
              ],
            },
          },
        ]}
      />
      {updateCatalog && (
        <UpdateCatalog
          action={updateCatalog.action}
          itemIds={updateCatalog.itemIds}
          onClose={() => setUpdateCatalog(null)}
        />
      )}
      {bulkDeleteItems && (
        <BulkDelete itemIds={bulkDeleteItems} onClose={() => setBulkDeleteItems(null)} />
      )}
      <Outlet />
    </Box>
  )
}
